import { Component, ElementRef, inject, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { ErrorService } from '../shared/error.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BytetounitPipe } from "../bytetounit.pipe";
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { CloudinaryUploadService } from './cloudinary-upload.service';

@Component({
  selector: 'app-upload-ui',
  imports: [MatIconModule, MatButtonModule, MatProgressSpinnerModule, BytetounitPipe],
  templateUrl: './upload-ui.component.html',
  styleUrl: './upload-ui.component.scss'
})
export class UploadUiComponent {

  // File upload restrictions
  photoAllowedFiles = /\.(jpg|jpeg|png|gif|pdf|docx|xlsx|txt)$/i;
  photoMaxFileSize = 52428800; // 50MB
  photoMaxFiles = 5;

  // Cloudinary Storage configuration for file uploads (from environment by default)
  cloudinaryCloudName?: string;
  cloudinaryUploadPreset?: string;




  @Input() allowedFiles: RegExp = this.photoAllowedFiles;
  @Input() allowedFilesCategoryLabel?: string
  @Input() maxFileSize: number = this.photoMaxFileSize; // in bytes
  @Input() maxFiles: number = this.photoMaxFiles;
  @Input() uploadFieldName: string = 'file';
  @Input() existingFiles?: any[]
  @Input() cloudName?: string;
  @Input() uploadPreset?: string;

  @Output() filesChanged = new EventEmitter<any[]>();

  allUploadingFiles: any[] = [];
  allUploadedFiles: any[] = [];




  private http = inject(HttpClient);
  private cloudinaryUploadService = inject(CloudinaryUploadService);
  ngOnChanges() {
    // Only sync from inputs when existingFiles is explicitly provided.
    if (this.existingFiles === undefined) return;

    const incoming = Array.isArray(this.existingFiles) ? this.existingFiles : [];
    const byKey = new Map<string, any>();
    for (const file of incoming) {
      if (!file) continue;
      const key = (file.fileLink ?? file.azureBlobName ?? `${file.uploadFileName ?? ''}|${file.uploadDate ?? ''}`).toString();
      if (!byKey.has(key)) byKey.set(key, file);
    }
    this.allUploadedFiles = Array.from(byKey.values());

  }
  errorService = inject(ErrorService);
  @ViewChild('fileToUpload') realInputFile!: ElementRef;
  selectFileToUpload(event: any): void {
// $#$    // /* alert('select file'); */
    this.realInputFile.nativeElement.value = "";
    this.realInputFile.nativeElement.click();

  }
  ngAfterViewInit() {

    this.realInputFile.nativeElement.onchange = (event: any) => {
      const files: FileList | undefined = event.target?.files;
      if (!files || files.length === 0) {
        console.log('No file selected');
        return;
      }

      console.log('Selected files:', files);
      for (const file of Array.from(files)) {
        if (this.allowedFiles && !this.allowedFiles.test(file.name)) {
          const supported = this.describeAllowedFiles();
          this.errorService.openandCloseError(`File type not allowed: ${file.name}. Supported files: ${supported}.`);
          continue;
        }

        if (this.maxFileSize && file.size > this.maxFileSize) {
          const limit = this.formatBytes(this.maxFileSize);
          this.errorService.openandCloseError(`File size exceeds the ${limit} limit: ${file.name}`);
          continue;
        }

        if (!this.canAcceptMoreFiles()) {
          this.errorService.openandCloseError(`Maximum number of files reached (${this.maxFiles}). Remove an attachment before uploading more.`);
          break;
        }

        this.queueFileForUpload(file);
        this.uploadToCloudinary(file);
      }
    }

  }



  deleteFile(fileLink: any, event: Event): void {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    this.allUploadedFiles = this.allUploadedFiles.filter(f => f.fileLink !== fileLink);
    console.log('Deleted file:', fileLink);
    this.filesChanged.emit(this.allUploadedFiles);
  }

  /**
   * Upload file to Cloudinary Storage using the Cloudinary service
   */
  private async uploadToCloudinary(file: File): Promise<void> {
    try {
      this.cloudinaryUploadService.uploadFile(file, this.cloudName, this.uploadPreset).subscribe({
        next: (result) => {
          // File uploaded successfully
          this.allUploadedFiles.push({
            ...file,
            fileLink: result.secure_url,
            uploadFileSize: result.bytes,
            uploadFileName: result.original_filename || file.name,
            uploadFileType: result.format ? `${result.resource_type}/${result.format}` : file.type,
            cloudinaryPublicId: result.public_id,
            uploadDate: result.created_at
          });

          // Remove from uploading files
          this.allUploadingFiles = this.allUploadingFiles.filter(f => f.uploadFileName !== file.name);

          console.log('✅ File uploaded to Cloudinary:', result.public_id);

          // Emit the updated files array
          this.filesChanged.emit(this.allUploadedFiles);
        },
        error: (error) => {
          console.error('❌ Error uploading file to Cloudinary:', error);
          this.errorService.openandCloseError(`Failed to upload file: ${file.name}. ${error.message}`);

          // Remove from uploading files on error
          this.allUploadingFiles = this.allUploadingFiles.filter(f => f.uploadFileName !== file.name);
        }
      });

    } catch (error) {
      console.error('❌ Error uploading file to Cloudinary:', error);
      this.errorService.openandCloseError(`Failed to upload file: ${file.name}`);

      // Remove from uploading files on error
      this.allUploadingFiles = this.allUploadingFiles.filter(f => f.uploadFileName !== file.name);
    }
  }

  /**
   * Delete file from Cloudinary Storage
   */
  async deleteCloudinaryFile(fileLink: string, cloudinaryPublicId: string): Promise<void> {
    this.cloudinaryUploadService.deleteFile(cloudinaryPublicId).subscribe({
      next: (success) => {
        if (success) {
          this.allUploadedFiles = this.allUploadedFiles.filter(f => f.fileLink !== fileLink);
          console.log('File deleted from Cloudinary:', cloudinaryPublicId);

          // Emit the updated files array
          this.filesChanged.emit(this.allUploadedFiles);
        }
      },
      error: (error) => {
        console.error('Error deleting file from Cloudinary:', error);
        this.errorService.openandCloseError('Failed to delete file from Cloudinary');
      }
    });
  }

  getFileExtension(mimeType: string): string {
    if (!mimeType) return '';
    // Extract extension from MIME type (e.g., "image/jpeg" -> "jpeg")
    const parts = mimeType.split('/');
    return parts.length > 1 ? parts[1] : mimeType;
  }

  getFileIcon(mimeType: string): string {
    if (!mimeType) return 'insert_drive_file';

    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('application/pdf')) return 'picture_as_pdf';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'description';
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return 'table_chart';
    if (mimeType.startsWith('text/')) return 'article';

    return 'insert_drive_file';
  }

  private queueFileForUpload(file: File): void {
    this.allUploadingFiles = [
      ...this.allUploadingFiles,
      { ...file, uploadFileSize: file.size, uploadFileName: file.name, uploadFileType: file.type }
    ];
  }

  private canAcceptMoreFiles(): boolean {
    if (!this.maxFiles) {
      return true;
    }
    const activeCount = this.allUploadedFiles.length + this.allUploadingFiles.length;
    return activeCount < this.maxFiles;
  }

  private describeAllowedFiles(): string {
    if (this.allowedFilesCategoryLabel) {
      return `${this.allowedFilesCategoryLabel} files`;
    }

    if (this.allowedFiles) {
      const pattern = this.allowedFiles.source;
      const groupMatch = pattern.match(/\(([^)]+)\)/);
      if (groupMatch && groupMatch[1]) {
        const extensions = groupMatch[1]
          .split('|')
          .map(ext => ext.startsWith('.') ? ext : `.${ext}`)
          .join(', ');
        return extensions;
      }
    }

    return 'the configured file types';
  }

  private formatBytes(bytes: number): string {
    if (!bytes || bytes <= 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, exponent);
    const precision = value >= 10 || exponent === 0 ? 0 : 1;
    return `${value.toFixed(precision)} ${units[exponent]}`;
  }

  private removeUploadingEntry(fileName: string): void {
    this.allUploadingFiles = this.allUploadingFiles.filter(f => f.uploadFileName !== fileName);
  }

}
